import { ScoreCriteriaType } from "../../schema/score.schema";
import { CellAssignment, PersonaView, SegmentGroup, SegmentView } from "../scoreConfigView";

/**
 * The overview matrix as data (PREC-4055): segment rows in three bands, persona
 * columns, and one of three states per cell. Pure, so the table component only
 * renders and the states are unit-tested here.
 */

export type CellState = "assigned" | "missing" | "none";

export interface MatrixBand {
	group: SegmentGroup;
	segments: SegmentView[];
}

const BAND_ORDER: SegmentGroup[] = ["PROSPECT", "PARTNER", "INVESTOR"];

/** The segments grouped into bands, in matrix order; a band the score does not have is omitted. */
export const bandsOf = (segments: SegmentView[]): MatrixBand[] =>
	BAND_ORDER.map((group) => ({
		group,
		segments: segments.filter((segment) => segment.group === group),
	})).filter((band) => band.segments.length > 0);

/** What a cell shows: the scoring it holds, "missing" when linked but empty, "none" when not linked. */
export const cellState = (linked: boolean, assignment: CellAssignment | null): CellState => {
	if (!linked) {
		return "none";
	}

	return assignment ? "assigned" : "missing";
};

/** The dot colour class of a scoring, by its position in the criteria list. */
export const SCORING_SWATCHES = [
	"bg-scoring-1",
	"bg-scoring-2",
	"bg-scoring-3",
	"bg-scoring-4",
	"bg-scoring-5",
	"bg-scoring-6",
	"bg-scoring-7",
	"bg-scoring-8",
] as const;

export const swatchOf = (criteria: ScoreCriteriaType[], criteriaId: number): string => {
	const index = criteria.findIndex((scoring) => scoring.id === criteriaId);

	return SCORING_SWATCHES[(index < 0 ? 0 : index) % SCORING_SWATCHES.length];
};

/** The options a cell's picker offers. */
export interface ScoringOption {
	id: number;
	name: string;
	locked: boolean;
	swatch: string;
}

export const scoringOptions = (criteria: ScoreCriteriaType[]): ScoringOption[] =>
	criteria.map((scoring) => ({
		id: scoring.id,
		name: scoring.name,
		locked: scoring.locked,
		swatch: swatchOf(criteria, scoring.id),
	}));
